import { useCallback, useMemo, useState } from 'react'
import SeeMoreOptions from '~containers/app/ask-a-vet/components/see-more-options'
import RadioSelect from '~containers/app/ask-a-vet/components/ui/radio-select'
import { useSelectExtraOptionsBottomsheet } from '~containers/app/ask-a-vet/hooks/use-select-extra-options-bottomsheet'
import { GroupItem, GroupSelectProps } from '~containers/app/ask-a-vet/types'
import SelectExtraOptionsBottomsheet from '../select-extra-options.bottomsheet'

const SeeMoreOptionsNumber = 12
const NumberOfRenderItemsWhenSeeMoreExist = 8

const SelectOptions = ({
  root,
  datafield,
  options,
  onSelect,
  useKeyAsLabel = false,
}: Readonly<GroupSelectProps>) => {
  const {
    openSelectOptionsBottomSheet,
    setOpenSelectOptionsBottomSheet,
    onCloseRequest,
    data,
    setData,
    value,
    onSelect: onSelectOptionSelect,
  } = useSelectExtraOptionsBottomsheet()
  const [hasExpanded, setExpanded] = useState(false)
  const showSeeMoreOptions = useMemo(
    () => options.length > SeeMoreOptionsNumber && !hasExpanded,
    [options, hasExpanded]
  )

  const renderItems = useMemo(
    () =>
      options.slice(0, !showSeeMoreOptions ? options.length : NumberOfRenderItemsWhenSeeMoreExist),
    [options, showSeeMoreOptions]
  )

  const _onSelect = useCallback(
    (selectedItem: GroupItem & { passive?: boolean }) => {
      onSelect({
        ...selectedItem,
        datafield: selectedItem.datafield || datafield,
        reset_other_datafields: !(
          selectedItem.datafield !== undefined && selectedItem.datafield !== datafield
        ),
      })

      if (selectedItem.extra_component?.type === 'select' && !selectedItem.passive) {
        setData(
          selectedItem.extra_component.options.map(option => ({
            label: option.label || option.value,
            value: option.value,
            datafield:
              selectedItem.extra_component?.datafield || selectedItem.datafield || datafield,
          }))
        )
        setOpenSelectOptionsBottomSheet(true)
      }
    },
    [datafield, onSelect, setData, setOpenSelectOptionsBottomSheet]
  )

  const onExtraComponentSelect = useCallback(
    (selectedItem: GroupItem) => {
      _onSelect(selectedItem)
      onSelectOptionSelect({
        label: selectedItem.label || '',
        value: selectedItem.value,
      })
    },
    [_onSelect, onSelectOptionSelect]
  )

  return (
    <div className="flex flex-col flex-gap-y-2 w-full">
      {renderItems.map((renderItem, index) => {
        const value = renderItem.datafield
          ? root[renderItem.datafield]
          : datafield
            ? root[datafield]
            : root
        const isSelected = useKeyAsLabel
          ? typeof value === 'string' && value === renderItem.label?.toLowerCase()
          : value === renderItem.value
        return (
          <RadioSelect
            key={index}
            root={root}
            item={renderItem}
            isSelected={isSelected}
            onSelect={_onSelect}
          />
        )
      })}
      {showSeeMoreOptions && <SeeMoreOptions onClick={() => setExpanded(true)} />}
      {openSelectOptionsBottomSheet && (
        <SelectExtraOptionsBottomsheet
          data={data}
          open={openSelectOptionsBottomSheet}
          onCloseRequest={onCloseRequest}
          value={value}
          onSelect={onExtraComponentSelect}
        />
      )}
    </div>
  )
}

export default SelectOptions
